import Player from './Player/Player';
import SceneManager from './Scene/SceneManager';
import LocaleHandler from './Utility/LocaleManager';
import ResourceManager from './Utility/Resource/ResourceManager';
import Ticker, { TickerEvent } from './Utility/Ticker';
import Environment from './Utility/Environment';
import StateManager from './State/StateManager';
import StyleHandler from './Utility/StyleManager';
import SDKManager from './Utility/SDK/SDKManager';
import PreloadScene from './Scene/PreloadScene';
import WorldScene from './Scene/WorldScene';
import LevelScene from './Scene/LevelScene';
import LuckyTurnipsScene from './Scene/LuckyTurnipsScene';
import { GameActions } from './State/Game/GameActions';
import TransitionScene from './Scene/TransitionScene';
import { IGameSettings, SETTINGS } from './Settings';
import { enableTracking } from './Utility/Tracking/DDNA';
import RenderSystem, { RenderSystemEvent } from './System/Render/RenderSystem';
import { WorldDataHelper } from './Utility/WorldDataHelper';
import { PlayerActions } from './State/Player/PlayerActions';
import { IGameState } from './State/Game/IGameState';
import { audioSceneId, farmTransitionSceneId, GameScene, preloadSceneId, transitionSceneId } from './State/Game/GameScene';
import MyPetMiniGameScene from './Scene/MyPetMiniGameScene';
import { ProgressStateHelper } from './State/Player/Progress/ProgressStateHelper';
import { Native } from '@sgorg/mobile-dev-tools';
import { createGameEmitter } from 'Utility/GameEvent/CreateGameEmitter';
import { initTracking } from 'Utility/Tracking/InitTracking';
import { IGameConfig } from 'Utility/IGameConfig';
import { Platform } from 'Utility/Platform';
import { CheatsToggle } from './Utility/CheatsGui/CheatsToggle';
import FarmScene from 'Scene/FarmScene';
import AudioScene from 'Scene/AudioScene';
import FarmTransitionScene from 'Scene/FarmTransitionScene';
import { LanguageKey } from 'Utility/SDK/Language';

export default class Game {
    public static player: Player;
    public static scene: SceneManager;
    public static resource: ResourceManager;
    public static store: StateManager;
    public static locale: LocaleHandler;
    public static style: StyleHandler;
    public static config: IGameConfig;
    public static ticker: Ticker;
    public static sdk: SDKManager;
    public static settings: IGameSettings;
    public static serverTimeOffset: number = 0;

    public static async init(config: IGameConfig) {
        Game.config = config;
        Game.store = new StateManager();
        Game.scene = new SceneManager();
        Game.locale = new LocaleHandler();
        Game.style = new StyleHandler();
        Game.resource = new ResourceManager();
        Game.player = new Player();
        Game.ticker = new Ticker();
        Game.settings = SETTINGS;

        Game.sdk = new SDKManager();
        await Game.sdk.initialized;

        

        // brute force stop all the gestures since apparently the css fix isn't good enough
        document.body.addEventListener("touchmove", ev => {
            if (ev.touches.length > 1) {
              ev.preventDefault();
              ev.stopImmediatePropagation();
            }
          }, true);


       // Howler.volume(0);
        //@ts-ignore
        FBInstant.setAdClosedCallback(()=> {
            console.log("ad failed to load")
            Howler.volume(1);
            //@ts-ignore
            FBInstant.setAdClosedCallback(()=> {});
        });

        //enableTracking();
        // Create global game event emitter for "external" game modules e.g. local notifications.
        createGameEmitter();
        // Initialize tracking handlers.
        //initTracking();

        Game.ticker.fpsLimit = Game.config.fpsLimit;
        config.language = getParameterByName("lang") as LanguageKey
        console.log(config.language)
        Game.locale.setLanguage(config.language);
        Game.ticker.start();
        Game.store.addListener({
            selector: (state: IGameState) => {
                return state;
            },
            callback: Game.handleStateChanged,
        });

        RenderSystem.init();

        // Start update loop.
        Game.ticker.on(TickerEvent.TICK, this.scene.update.bind(this.scene));

        Game.scene.addScene(GameScene.LEVEL, new LevelScene());
        Game.scene.addScene(GameScene.WORLD, new WorldScene());
        Game.scene.addScene(transitionSceneId, new TransitionScene(1));
        Game.scene.addScene(GameScene.LUCKY_TURNIPS, new LuckyTurnipsScene());
        Game.scene.addScene(GameScene.FARM, new FarmScene());
        Game.scene.addScene(farmTransitionSceneId, new FarmTransitionScene(1));
        Game.scene.addScene(preloadSceneId, new PreloadScene());

        Game.scene.addScene(GameScene.MY_PET_MINI_GAME, new MyPetMiniGameScene());
        Game.scene.addScene(audioSceneId, new AudioScene());

        await Game.scene.startScene(preloadSceneId);
        await Game.scene.startScene(audioSceneId, {}, true);

        Native.main.closeSplashScreen();
        new CheatsToggle();
    }

    
    /**
     * Called after initial preload is complete.
     */
    public static start(): void {
        if ([Platform.FACEBOOK, Platform.LOCAL].includes(Game.config.platform)) {
            document.getElementById('canvas').style.background = 'none';
        }

        if (process?.env.NODE_ENV === Environment.DEVELOPMENT && this.debugLoadSpecificLevelIfRequested()) {
            // nothing to do
        } else if (ProgressStateHelper.getLevelStars(Game.store.getState(), 1) === 0) {
            // Load first level.
            GameActions.loadLevel({ levelNumber: 1, preBoosters: [] });
        } else {
            this.unlockNextWorldIfPossible();

            if (this.isLuckyTurnipsInProgress()) {
                GameActions.loadLuckyTurnips();
            } else {
                // Load active world.
                GameActions.loadWorld({
                    clearUI: false,
                    firstLoad: true,
                });
            }
        }

        Game.sdk.onStart();
    }

    private static debugLoadSpecificLevelIfRequested() {
        const debugLevelNumber = Number(window.location.hash.slice(1));
        if (debugLevelNumber > 0) {
            GameActions.loadLevel({
                levelNumber: debugLevelNumber,
                preBoosters: [],
            });
            return true;
        }
        return false;
    }

    private static unlockNextWorldIfPossible() {
        const state = Game.store.getState();

        const atMaxWorldLevel =
            Game.player.getHighestLevelCompleted(state) >= WorldDataHelper.maxLevel(state.worldState.worldNumber);

        const nextMapExists = Boolean(WorldDataHelper.getWorldData(state.worldState.worldNumber + 1));

        if (atMaxWorldLevel && nextMapExists) {
            PlayerActions.setPlayerState(ProgressStateHelper.unlockNextWorld(state.playerState));
        }
    }

    private static isLuckyTurnipsInProgress() {
        return Game.store.getState().playerState.luckyTurnipsState !== null;
    }

    public static handleStateChanged(state: IGameState) {
        // Check if we can start the game.
        if (!Game.store.getPreviousState().gameLoaded && state.gameLoaded) {
            Game.start();
        }

        Game.player.handleStateChanged(state);
        Game.scene.handleStateChanged(state);
    }
}

export function getParameterByName(name:string, url = window.location.href) {
    name = name.replace(/[\[\]]/g, '\\$&');
    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

